import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useMessage } from "../contexts/MessageContext";
import { useAuth } from "../contexts/AuthContext";
import { Loading } from "./common/Loading";

const apiUrl = window._env_?.REACT_APP_API_URL || "https://app.mylocaleventrentals.com/api";

const CUSTOMER_UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const emptyLine = () => ({
  inventory_id: "",
  description: "",
  quantity: 1,
  unit_price: 0,
  total: 0,
});

const InvoiceCreate = () => {
  const { uuid } = useParams();
  const [searchParams] = useSearchParams();
  const isEdit = Boolean(uuid);
  const [customers, setCustomers] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [invoiceLines, setInvoiceLines] = useState([emptyLine()]);
  const [discount, setDiscount] = useState({ type: 'fixed', value: 0 });
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const { logout } = useAuth();
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const { showSuccess, showError } = useMessage();

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      logout();
      navigate("/login");
      return;
    }

    const fetchData = async () => {
      try {
        const [custRes, invRes] = await Promise.all([
          axios.get(apiUrl + "/customers", { headers: { "x-auth-token": token } }),
          axios.get(apiUrl + "/inventory", { headers: { "x-auth-token": token } })
        ]);
        setCustomers(custRes.data?.data || []);
        setInventory(invRes.data?.items || []);

        if (!isEdit) {
          const presetId = searchParams.get("customerId");
          if (presetId && CUSTOMER_UUID_RE.test(presetId)) {
            try {
              const cRes = await axios.get(`${apiUrl}/customers/${presetId}`, {
                headers: { "x-auth-token": token },
              });
              const c = cRes.data?.data;
              if (c?.uuid) {
                setCustomerId(c.uuid);
                setCustomers((prev) => {
                  if (prev.some((x) => x.uuid === c.uuid)) return prev;
                  return [...prev, c];
                });
              }
            } catch (_) {
              /* optional preset */
            }
          }
        }

        if (isEdit) {
          const invDataRes = await axios.get(`${apiUrl}/invoice/${uuid}`, {
            headers: { "x-auth-token": token }
          });
          const invoice = invDataRes.data?.data || {};
          setCustomerId(invoice.customer_id || invoice.Customer?.uuid || "");

          const lines = (invoice.InvoiceLines || []).map((line) => ({
            inventory_id: line.item_id || "",
            description: line.description || "",
            quantity: parseFloat(line.quantity) || 1,
            unit_price: parseFloat(line.unit_price) || 0,
            total: (parseFloat(line.quantity) || 1) * (parseFloat(line.unit_price) || 0),
          }));

          setInvoiceLines(lines.length ? lines : [emptyLine()]);
          setDiscount({
            type: invoice.discount_type || "fixed",
            value: parseFloat(invoice.discount_value) || 0,
          });
        }
      } catch (err) {
        console.error("Invoice form load failed:", err);
        setError(isEdit ? "Failed to load invoice details" : "Failed to load form data");
        showError(isEdit ? "Failed to load invoice details" : "Failed to load form data");
      }
      setLoading(false);
    };
    fetchData();
  }, [isEdit, navigate, logout, showError, uuid, searchParams]);

  const totalAmount = useMemo(() => {
    const subtotal = invoiceLines.reduce((sum, line) => sum + (line.quantity * line.unit_price), 0);
    const dv = parseFloat(discount.value) || 0;
    const discountAmt = discount.type === 'percent' ? subtotal * dv / 100 : dv;
    return Math.max(0, subtotal - discountAmt);
  }, [invoiceLines, discount]);

  const handleLineChange = (index, field, value) => {
    const newLines = [...invoiceLines];

    if (field === "inventory_id") {
      newLines[index][field] = value;
      const selectedItem = inventory.find(item => item.uuid === value);
      if (selectedItem) {
        newLines[index].description = selectedItem.name || "";
        newLines[index].unit_price = selectedItem.price || 0;
      }
    } else if (field === "description") {
      newLines[index].description = value;
    } else {
      newLines[index][field] = parseFloat(value) || 0;
    }

    newLines[index].total = newLines[index].quantity * newLines[index].unit_price;
    setInvoiceLines(newLines);
  };

  const addLine = () => setInvoiceLines([...invoiceLines, emptyLine()]);
  const removeLine = (index) => setInvoiceLines(invoiceLines.filter((_, i) => i !== index));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!customerId) return setError("Select a customer");

    const token = localStorage.getItem("token");

    try {
      setSaving(true);
      const payload = {
        customer_id: customerId,
        invoice_lines: invoiceLines.map((line) => ({
          inventory_id: line.inventory_id || null,
          description: line.description || "",
          quantity: parseFloat(line.quantity) || 0,
          unit_price: parseFloat(line.unit_price) || 0,
        })),
        discount_type: discount.type,
        discount_value: parseFloat(discount.value) || 0,
      };

      if (isEdit) {
        await axios.put(`${apiUrl}/invoice/${uuid}`, payload, {
          headers: { "x-auth-token": token },
        });
        showSuccess("Invoice updated successfully!");
        navigate(`/invoice/${uuid}`);
      } else {
        const res = await axios.post(apiUrl + "/invoice", payload, {
          headers: { "x-auth-token": token },
        });
        showSuccess("Invoice created successfully!");
        const newUuid = res.data?.data?.uuid;
        navigate(newUuid ? `/invoice/${newUuid}` : "/invoice");
      }
    } catch (err) {
      console.error(err);
      const message = err.response?.data?.message || err.response?.data?.error || `Failed to ${isEdit ? "update" : "create"} invoice`;
      setError(message);
      showError(message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loading text={isEdit ? "Loading invoice..." : "Loading form..."} />;

  return (
    <div className="p-4 md:p-6 my-2 md:my-6 bg-white shadow-lg rounded-lg">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl md:text-3xl font-bold text-gray-800">
          {isEdit ? "Edit Invoice" : "Create Invoice"}
        </h2>
        <button
          type="button"
          onClick={() => navigate(isEdit ? `/invoice/${uuid}` : "/invoice")}
          className="btn-secondary"
        >
          Cancel
        </button>
      </div>
      {error && <p className="text-red-500 mb-4">{error}</p>}

      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Customer</label>
          <select
            value={customerId}
            onChange={(e) => setCustomerId(e.target.value)}
            className="border border-gray-300 p-2 rounded-md w-full"
            disabled={saving}
          >
            <option value="">Select Customer</option>
            {customers.map((c) => (
              <option key={c.uuid} value={c.uuid}>
                {c.firstname} {c.lastname}
              </option>
            ))}
          </select>
        </div>

        <div className="overflow-x-auto">
        <table className="min-w-full mb-4 border border-gray-200 rounded-lg overflow-hidden">
          <thead className="bg-gray-100 text-sm text-gray-700">
            <tr>
              <th className="p-2 text-left">Item</th>
              <th className="p-2 text-left">Description</th>
              <th className="p-2 text-left">Qty</th>
              <th className="p-2 text-left">Unit Price</th>
              <th className="p-2 text-left">Total</th>
              <th className="p-2 text-left">Action</th>
            </tr>
          </thead>
          <tbody>
            {invoiceLines.map((line, idx) => (
              <tr key={idx}>
                <td>

                  <select
                    value={line.inventory_id}
                    onChange={(e) => handleLineChange(idx, "inventory_id", e.target.value)}
                    className="border border-gray-300 rounded p-1 w-full"
                    disabled={saving}
                  >
                    <option value="">Select Item</option>
                    {inventory.map((item) => (
                      <option key={item.uuid} value={item.uuid}>
                        {item.name}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <input
                    type="text"
                    value={line.description}
                    onChange={(e) => handleLineChange(idx, "description", e.target.value)}
                    className="border border-gray-300 rounded p-1 w-full"
                    disabled={saving}
                  />
                </td>
                <td>
                  <input
                    type="number"
                    value={line.quantity}
                    onChange={(e) => handleLineChange(idx, "quantity", e.target.value)}
                    className="border border-gray-300 rounded p-1 w-16"
                    disabled={saving}
                  />
                </td>
                <td>
                  <input
                    type="number"
                    value={line.unit_price}
                    onChange={(e) => handleLineChange(idx, "unit_price", e.target.value)}
                    className="border border-gray-300 rounded p-1 w-24"
                    disabled={saving}
                  />
                </td>
                <td className="text-sm text-gray-700">${line.total.toFixed(2)}</td>
                <td>
                  <button
                    type="button"
                    onClick={() => removeLine(idx)}
                    className="text-red-600 hover:text-red-800 text-sm"
                    disabled={saving || invoiceLines.length === 1}
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>

        <button
          type="button"
          onClick={addLine}
          className="mb-4 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
          disabled={saving}
        >
          Add Line
        </button>

        {/* Discount */}
        <div className="mb-4 flex items-center gap-3">
          <label className="text-sm font-medium text-gray-700">Discount:</label>
          <button
            type="button"
            onClick={() => setDiscount(d => ({ ...d, type: 'fixed' }))}
            className={`px-3 py-1 rounded border ${discount.type === 'fixed' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700'}`}
            disabled={saving}
          >$</button>
          <button
            type="button"
            onClick={() => setDiscount(d => ({ ...d, type: 'percent' }))}
            className={`px-3 py-1 rounded border ${discount.type === 'percent' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700'}`}
            disabled={saving}
          >%</button>
          <input
            type="number"
            min="0"
            value={discount.value}
            onChange={(e) => setDiscount(d => ({ ...d, value: e.target.value }))}
            className="border border-gray-300 p-1 w-28 rounded"
            placeholder="0"
            disabled={saving}
          />
        </div>

        <div className="mb-4 font-bold">Total Amount: ${totalAmount.toFixed(2)}</div>

        <button type="submit" disabled={saving} className="btn-primary">
          {saving ? (isEdit ? "Updating..." : "Creating...") : (isEdit ? "Update Invoice" : "Create Invoice")}
        </button>
      </form>
    </div>
  );
};

export default InvoiceCreate;